/**
 * The letter desk (WPS-231): the parts of the composer a letter uses.
 *
 * - `TemplateRail`: every letter, email or request the record can send, each
 *   under its heading. A letter's heading is its category - the first of its
 *   tags, authored on the template in Settings - so a tenant files its own
 *   letters and nothing here names one.
 * - `LetterPage`: the letter as a page on the tenant's letterhead, filled in
 *   from the record. The date, addressee, address and reference are typed over
 *   in place and the body is typed into like a document - never HTML.
 *
 * Tenant-agnostic: categories, labels and the letterhead all come from the API.
 */
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronRight, Loader2, Search, Sparkles } from 'lucide-react'

import { EmailEditor } from '@/admin/components/email-editor'
import {
  SUGGESTED_CATEGORY,
  dedupe,
  type LetterCategory,
  type LetterPageValue,
} from '@/admin/components/composer/letter-desk'
import type { ComposerTemplate, Letterhead } from '@/admin/hooks/use-composer'
import { Button } from '@/shared/components/ui/button'
import { DatePicker } from '@/shared/components/ui/date-picker'
import { Input } from '@/shared/components/ui/input'
import { Textarea } from '@/shared/components/ui/textarea'

const RAIL_LABEL = 'text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]'

/**
 * The library: a search, then each group under its heading - the letters,
 * emails or requests in it indented beneath. The group in use is open and the
 * rest fold away; a search looks across every group.
 */
export function TemplateRail({
  sections,
  search,
  onSearch,
  selectedId,
  onSelect,
  loading,
  error,
  onRetry,
  searchPlaceholder,
  emptyLabel,
  noMatchesLabel,
  footer,
}: {
  sections: LetterCategory[]
  search: string
  onSearch: (term: string) => void
  selectedId: number | null
  onSelect: (id: number) => void
  loading: boolean
  error: boolean
  onRetry: () => void
  searchPlaceholder: string
  emptyLabel: string
  noMatchesLabel: string
  /** Under the search, e.g. a way into template authoring. */
  footer?: ReactNode
}) {
  const { t } = useTranslation()
  const term = search.trim().toLowerCase()
  // A template can sit under several headings (the same email in each claim
  // type that sends it); only the heading it was picked from shows it picked.
  const [pickedIn, setPickedIn] = useState<string | null>(null)
  const homeKey =
    pickedIn ??
    sections.find((section) => section.items.some((item) => item.id === selectedId))?.key ??
    null
  // Open: the first group, and the one the template was picked from.
  const [opened, setOpened] = useState<Record<string, boolean>>({})
  const isOpen = (section: LetterCategory, index: number) =>
    opened[section.key] ?? (index === 0 || section.key === homeKey)

  const matches = term
    ? dedupe(sections.flatMap((section) => section.items)).filter(
        (item) =>
          item.name.toLowerCase().includes(term) ||
          item.subject.toLowerCase().includes(term) ||
          (item.when_label ?? '').toLowerCase().includes(term) ||
          (item.tags ?? []).some((tag) => tag.toLowerCase().includes(term)),
      )
    : []

  return (
    <div className="flex min-h-0 flex-col gap-2 px-3 py-1">
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-[var(--ink-3)]" />
        <Input
          value={search}
          onChange={(event) => onSearch(event.target.value)}
          placeholder={searchPlaceholder}
          className="h-9 rounded-[8px] pl-8 text-[13px]"
          data-testid="letter-search"
        />
      </div>
      {footer}

      {loading ? (
        <div className="flex items-center gap-2 py-4 text-[12px] text-[var(--ink-3)]">
          <Loader2 size={14} className="animate-spin" />
          {t('claimLetter.loading', 'Loading letters...')}
        </div>
      ) : error ? (
        <div className="flex flex-col items-start gap-2 py-4 text-[12px] text-[var(--ink-2)]">
          {t('claimLetter.error', "Couldn't load the letter library.")}
          <Button variant="outline" size="xs" onClick={onRetry}>
            {t('common.retry', 'Retry')}
          </Button>
        </div>
      ) : sections.length === 0 ? (
        <p className="py-4 text-[12px] text-[var(--ink-3)]">{emptyLabel}</p>
      ) : term ? (
        <div className="flex flex-col gap-0.5 pt-1">
          <p className={`${RAIL_LABEL} px-1 pb-1`}>
            {t('claimLetter.searchResults', 'Matching letters')}
          </p>
          {matches.length === 0 ? (
            <p className="px-1 py-2 text-[12px] text-[var(--ink-3)]">{noMatchesLabel}</p>
          ) : (
            matches.map((item) => (
              <RailItem
                key={item.id}
                item={item}
                active={item.id === selectedId}
                onSelect={onSelect}
              />
            ))
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-1 pt-1" data-testid="letter-categories">
          {sections.map((section, index) => {
            const open = isOpen(section, index)
            return (
              <div key={section.key} className="flex flex-col">
                <Button
                  variant="ghost"
                  data-testid="letter-category"
                  aria-expanded={open}
                  onClick={() => setOpened((prev) => ({ ...prev, [section.key]: !open }))}
                  className="flex h-auto w-full items-center gap-2 rounded-[8px] px-2 py-2 text-left font-normal hover:bg-[var(--bg-subtle)]"
                >
                  <ChevronRight
                    className={`h-3.5 w-3.5 shrink-0 text-[var(--ink-3)] transition-transform ${
                      open ? 'rotate-90' : ''
                    }`}
                  />
                  {section.key === SUGGESTED_CATEGORY && (
                    <Sparkles className="h-3.5 w-3.5 shrink-0 text-[var(--green)]" />
                  )}
                  <span className="min-w-0 flex-1 truncate text-[13px] text-[var(--ink)]">
                    {section.label}
                  </span>
                  <span className="shrink-0 rounded-full bg-[var(--bg-subtle)] px-2 py-px text-[11px] text-[var(--ink-3)]">
                    {section.items.length}
                  </span>
                </Button>
                {open && (
                  <div className="ml-[15px] flex flex-col gap-0.5 border-l-[0.5px] border-l-[var(--rule)] py-0.5 pl-2">
                    {section.items.map((item) => (
                      <RailItem
                        key={`${section.key}-${item.id}`}
                        item={item}
                        active={item.id === selectedId && section.key === homeKey}
                        onSelect={(id) => {
                          setPickedIn(section.key)
                          onSelect(id)
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

/** One template in the rail: its name, and when or why it is used. */
function RailItem({
  item,
  active,
  onSelect,
}: {
  item: ComposerTemplate
  active: boolean
  onSelect: (id: number) => void
}) {
  const detail = (item.tags ?? []).slice(1).join(' - ') || item.when_label
  return (
    <Button
      variant="ghost"
      data-testid="compose-template-option"
      data-active={active || undefined}
      onClick={() => onSelect(item.id)}
      className={`flex h-auto w-full flex-col items-start gap-0.5 rounded-[6px] px-2.5 py-1.5 text-left font-light ${
        active
          ? 'bg-[var(--green-bg-muted)] text-[var(--green)]'
          : 'text-[var(--ink-2)] hover:bg-[var(--bg-subtle)]'
      }`}
    >
      <span className="w-full truncate text-[13px]">{item.name}</span>
      {detail && (
        <span
          className={`w-full truncate text-[11px] ${
            active ? 'text-[var(--green)]' : 'text-[var(--ink-3)]'
          }`}
        >
          {detail}
        </span>
      )}
    </Button>
  )
}

// Header inputs read as the letter, not as a form: no box until you use them.
const PAGE_FIELD =
  'h-auto rounded-[3px] border-0 border-b-[0.5px] border-dashed border-b-[var(--green)] bg-transparent px-1 py-0.5 font-document text-[13px] text-[var(--ink)] shadow-none focus-visible:bg-[var(--bg-light)] focus-visible:ring-0'

/** The letter as a page on the tenant's letterhead, typed into in place. */
export function LetterPage({
  value,
  onChange,
  letterhead,
  referenceLabel,
  error,
}: {
  value: LetterPageValue
  onChange: (next: LetterPageValue) => void
  letterhead?: Letterhead
  referenceLabel: string
  error?: string
}) {
  const { t } = useTranslation()
  // A logo that cannot load is left off rather than shown broken.
  const [logoFailed, setLogoFailed] = useState(false)
  return (
    <div className="flex flex-col gap-2" data-testid="letter-page">
      <div className="rounded-[10px] bg-[var(--bg-subtle)] p-3 md:p-5">
        <div className="mx-auto flex max-w-[680px] flex-col gap-5 rounded-[4px] border-[0.5px] border-[var(--rule)] bg-white px-6 py-7 font-document md:px-10 md:py-9">
          {/* Letterhead: whose paper it is on. */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-0.5 text-[10px] uppercase tracking-[0.06em] text-[var(--ink-3)]">
              {letterhead?.name && (
                <span className="font-medium text-[var(--ink-2)]">{letterhead.name}</span>
              )}
              {letterhead?.address
                ?.split('\n')
                .filter(Boolean)
                .map((line) => <span key={line}>{line}</span>)}
            </div>
            {letterhead?.logo_url && !logoFailed && (
              <img
                src={letterhead.logo_url}
                alt={letterhead.name}
                onError={() => setLogoFailed(true)}
                className="h-12 w-auto max-w-[140px] object-contain"
              />
            )}
          </div>

          {/* Date, addressee, address and reference: filled in, typed over. */}
          <div className="flex flex-col items-start gap-1.5">
            <div className="w-[190px]" data-testid="letter-date">
              <DatePicker
                value={value.date}
                onChange={(date) => onChange({ ...value, date })}
                className="h-8 text-[13px]"
              />
            </div>
            <Input
              value={value.name}
              aria-label={t('claimLetter.addressedTo', 'Addressed to')}
              placeholder={t('claimLetter.addressedTo', 'Addressed to')}
              data-testid="letter-name"
              onChange={(event) =>
                onChange({
                  ...value,
                  name: event.target.value,
                  touched: { ...value.touched, name: true },
                })
              }
              className={`${PAGE_FIELD} w-full max-w-[360px]`}
            />
            <Textarea
              value={value.address}
              aria-label={t('claimLetter.address', 'Address')}
              placeholder={t('claimCompose.addressPlaceholder', 'Street, town and postcode')}
              data-testid="letter-address"
              rows={Math.max(2, value.address.split('\n').length)}
              onChange={(event) =>
                onChange({
                  ...value,
                  address: event.target.value,
                  touched: { ...value.touched, address: true },
                })
              }
              className={`${PAGE_FIELD} min-h-0 w-full max-w-[360px] resize-none leading-[1.5]`}
            />
            <div className="flex items-baseline gap-1 text-[13px] text-[var(--ink)]">
              {referenceLabel && <span>{referenceLabel} #:</span>}
              <Input
                value={value.reference}
                aria-label={t('claimLetter.reference', 'Reference')}
                data-testid="letter-reference"
                onChange={(event) => onChange({ ...value, reference: event.target.value })}
                className={`${PAGE_FIELD} w-[200px]`}
              />
            </div>
          </div>

          {/* The body: typed into like a document. */}
          <div className="letter-page-body">
            <EmailEditor
              value={value.body}
              onChange={(body) =>
                onChange({ ...value, body, touched: { ...value.touched, body: true } })
              }
              defaultMode="visual"
              typingOnly
              minHeight={260}
              maxHeight={560}
            />
          </div>
        </div>
      </div>
      <p className="text-[11px] text-[var(--ink-3)]">
        {t(
          'claimLetter.pageHint',
          'Click anywhere on the page to type. The underlined lines were filled in from the record. Changes apply to this letter only.',
        )}
      </p>
      {error && (
        <p className="text-[11px] text-[var(--status-error)]" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
